import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Package, ShoppingCart, Briefcase, BarChart3 } from "lucide-react";
import { api, adminHeaders } from "../lib/api";
import { AdminStats } from "../components/admin/AdminStats";
import { AdminB2BTab } from "../components/admin/AdminB2BTab";
import { AdminOrdersTab } from "../components/admin/AdminOrdersTab";
import { AdminProductsTab } from "../components/admin/AdminProductsTab";

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem("admin_key"));
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [b2b, setB2b] = useState([]);
  const [b2bSettings, setB2bSettings] = useState({ min_order: 250, carton_qty: 6 });
  const [stats, setStats] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/orders", { headers: adminHeaders() }).then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/b2b", { headers: adminHeaders() }).then((r) => setB2b(r.data)).catch(() => {});
    api.get("/admin/b2b-settings", { headers: adminHeaders() }).then((r) => setB2bSettings(r.data)).catch(() => {});
    api.get("/admin/stats", { headers: adminHeaders() }).then((r) => setStats(r.data)).catch(() => {});
    api.get("/products", { params: { include_unavailable: true } }).then((r) => setProducts(r.data));
  }, []);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/admin/login", { password });
      sessionStorage.setItem("admin_key", r.data.token);
      setAuthed(true);
    } catch {
      toast.error("Password errata");
    }
  };

  const updateOrder = async (id, status) => {
    await api.patch(`/admin/orders/${id}`, { status }, { headers: adminHeaders() });
    toast.success("Ordine aggiornato");
    load();
  };

  const saveTracking = async (id, carrier, number) => {
    await api.patch(`/admin/orders/${id}`, { tracking_carrier: carrier, tracking_number: number }, { headers: adminHeaders() });
    toast.success("Tracking salvato");
    load();
  };

  const updateB2B = async (id, status, discount) => {
    const body = {};
    if (status) body.status = status;
    if (discount !== undefined) body.discount = discount;
    await api.patch(`/admin/b2b/${id}`, body, { headers: adminHeaders() });
    toast.success("Account aggiornato");
    load();
  };

  const updateB2BSettings = async (body) => {
    const r = await api.patch("/admin/b2b-settings", body, { headers: adminHeaders() });
    setB2bSettings(r.data);
    toast.success("Impostazioni B2B aggiornate");
  };

  const patchProduct = async (slug, updates) => {
    try {
      await api.patch(`/admin/products/${slug}`, updates, { headers: adminHeaders() });
      toast.success("Prodotto aggiornato");
      load();
    } catch {
      toast.error("Errore aggiornamento");
    }
  };

  const deleteProduct = async (slug) => {
    if (!window.confirm("Eliminare questo prodotto?")) return;
    await api.delete(`/admin/products/${slug}`, { headers: adminHeaders() });
    toast.success("Prodotto eliminato");
    load();
  };

  const createProduct = async (payload) => {
    try {
      await api.post("/admin/products", payload, { headers: adminHeaders() });
      toast.success("Prodotto creato");
      load();
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione");
      return false;
    }
  };

  if (!authed)
    return (
      <div data-testid="admin-login-page" className="max-w-sm mx-auto px-6 py-24">
        <div className="w-12 h-12 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-6"><Lock className="w-5 h-5 text-[#2E8A57]" /></div>
        <h1 className="font-serif-display font-bold text-3xl mb-2">Area Admin</h1>
        <p className="text-sm text-[#78716C] mb-8">Inserisci la password amministratore.</p>
        <form onSubmit={login} className="space-y-4">
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full rounded-xl border border-[#E7E0D8] bg-white px-4 py-3.5 text-sm focus:outline-none focus:border-[#2E8A57]"
          />
          <button data-testid="admin-login-btn" type="submit" className="btn-tuscan w-full py-4 rounded-full text-sm font-semibold">Accedi</button>
        </form>
      </div>
    );

  const TABS = [
    ["orders", ShoppingCart, `Ordini (${orders.length})`],
    ["products", Package, `Prodotti (${products.length})`],
    ["b2b", Briefcase, `B2B (${b2b.length})`],
    ["stats", BarChart3, "Statistiche"],
  ];

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center justify-between mb-10">
        <h1 className="font-serif-display font-bold text-3xl sm:text-4xl">Pannello Admin</h1>
        <button data-testid="admin-logout-btn" onClick={() => { sessionStorage.removeItem("admin_key"); setAuthed(false); }} className="text-sm text-[#78716C] hover:text-[#2E8A57]">Esci</button>
      </div>
      <div className="flex gap-2 mb-8">
        {TABS.map(([key, Icon, label]) => (
          <button
            key={key}
            data-testid={`admin-tab-${key}`}
            onClick={() => setTab(key)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium border transition-colors ${tab === key ? "bg-[#2E8A57] text-white border-[#2E8A57]" : "border-[#E7E0D8]"}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "stats" && stats && <AdminStats stats={stats} />}
      {tab === "b2b" && <AdminB2BTab accounts={b2b} settings={b2bSettings} onUpdateAccount={updateB2B} onUpdateSettings={updateB2BSettings} />}
      {tab === "orders" && <AdminOrdersTab orders={orders} onUpdateStatus={updateOrder} onSaveTracking={saveTracking} />}
      {tab === "products" && <AdminProductsTab products={products} onPatch={patchProduct} onDelete={deleteProduct} onCreate={createProduct} />}
    </div>
  );
}
